import type { ITfGamePlayConfig } from './map-tf-configs-structs';
import { ObstacleTileStage, type TerraformingNavMeshId, terraformingNavMeshIds } from './terraforming-match';

/**
 * Match-wide obstruction growth uses {@link ObstacleTileStage} on the schema `obstacleTier` field.
 *
 * Sprout is the starting state and is walkable. Sapling blocks walking; seeds still pass.
 * Tree blocks walking and seed effects. None is a per-tile POI marker, not a match growth state.
 *
 * Config `obstacleTier1Threshold` unlocks Sapling. `obstacleTier2Threshold` unlocks Tree.
 */
export type TfObstacleMatchStage = ObstacleTileStage.Sprout | ObstacleTileStage.Sapling | ObstacleTileStage.Tree;

/** Grass-goal progress ratios that advance match obstruction from Sprout to Sapling and Tree. */
export type TfObstacleGrowthThresholds = {
  readonly [ObstacleTileStage.Sapling]: number;
  readonly [ObstacleTileStage.Tree]: number;
};

export function isTfObstacleMatchStage(value: unknown): value is TfObstacleMatchStage {
  return value === ObstacleTileStage.Sprout
    || value === ObstacleTileStage.Sapling
    || value === ObstacleTileStage.Tree;
}

export function tfObstacleGrowthThresholdsFromConfig(
  config: Pick<ITfGamePlayConfig, 'obstacleTier1Threshold' | 'obstacleTier2Threshold'>
): TfObstacleGrowthThresholds {
  return {
    [ObstacleTileStage.Sapling]: config.obstacleTier1Threshold,
    [ObstacleTileStage.Tree]: config.obstacleTier2Threshold
  };
}

/** Resolves match obstruction from grass-goal progress. Below Sapling threshold is Sprout. */
export function tfObstacleTierFromGrassProgress(grassGoalProgressRatio: number, thresholds: TfObstacleGrowthThresholds): TfObstacleMatchStage {
  if (grassGoalProgressRatio >= thresholds[ObstacleTileStage.Tree]) {
    return ObstacleTileStage.Tree;
  }

  if (grassGoalProgressRatio >= thresholds[ObstacleTileStage.Sapling]) {
    return ObstacleTileStage.Sapling;
  }

  return ObstacleTileStage.Sprout;
}

export function tfObstacleTierBlocksMovement(stage: ObstacleTileStage): boolean {
  return stage >= ObstacleTileStage.Sapling;
}

export function tfObstacleTierBlocksSeedEffects(stage: ObstacleTileStage): boolean {
  return stage >= ObstacleTileStage.Tree;
}

export function tfFlowerStationUnlocked(stage: ObstacleTileStage): boolean {
  return tfObstacleTierBlocksMovement(stage);
}

export function tfNavMeshIdForObstacleTier(stage: ObstacleTileStage): TerraformingNavMeshId {
  return tfObstacleTierBlocksMovement(stage) ? terraformingNavMeshIds.blocked : terraformingNavMeshIds.open;
}
